import { challengeQueries } from '@therame/api-client';
import { record } from 'rrweb';
import type { eventWithTime } from 'rrweb';
import { onBeforeUnmount, ref } from 'vue';

type ListenerHandler = () => void;

const BATCH_SEND_INTERVAL_MS = 5_000;
const CONSENT_ACCEPTED_KEY = 'challenge_consent_accepted';

/**
 * rrweb-based session recorder for Challenge Mode.
 *
 * Lifecycle:
 *   1. caller invokes `start()` AFTER the consent gate resolves successfully
 *      (the gate POSTs `/challenge/session/:id/consent`).
 *   2. rrweb's `record()` runs with input + canvas defaults; password fields
 *      are masked by rrweb itself.
 *   3. events buffer locally; every 5s OR when the buffer reaches 100 events,
 *      we POST a JSON batch to `/challenge/session/:id/replay` via axios.
 *   4. on `pagehide` / `visibilitychange === 'hidden'` we flush via
 *      `navigator.sendBeacon` (fetch isn't reliable during teardown).
 *
 * No retry logic: the participant is short-lived and the rrweb stream is
 * inherently lossy, so dropping a batch on transient failure is acceptable.
 */
export function useChallengeRecorder() {
  const isRecording = ref(false);
  let stopFn: ListenerHandler | undefined;
  let buffer: eventWithTime[] = [];
  let seq = 0;
  let intervalId: ReturnType<typeof setInterval> | undefined;
  let sessionId: string | null = null;

  const flush = async (): Promise<void> => {
    if (!sessionId || buffer.length === 0) return;
    const batch = buffer;
    buffer = [];
    const localSeq = seq++;
    try {
      await challengeQueries.sendReplayBatch(sessionId, localSeq, batch);
    } catch {
      // Intentionally swallowed — see lifecycle comment above.
    }
  };

  /**
   * `sendBeacon`-based flush for the tab-close path. We can't `await` the
   * axios promise inside `pagehide` (the browser tears down the runtime), so
   * we hand the JSON to the browser and forget. APISIX is reachable at the
   * same origin so a relative URL is fine.
   */
  const beaconFlush = (apiRoot: string): void => {
    if (!sessionId || buffer.length === 0) return;
    const url = `${apiRoot}/challenge/session/${sessionId}/replay`;
    const localSeq = seq++;
    const blob = new Blob([JSON.stringify({ seq: localSeq, events: buffer })], {
      type: 'application/json',
    });
    try {
      navigator.sendBeacon(url, blob);
    } catch {
      // Tab is closing — nothing useful we can do here.
    }
    buffer = [];
  };

  const onVisibilityHidden = (apiRoot: string) => () => {
    if (document.visibilityState === 'hidden') {
      beaconFlush(apiRoot);
    }
  };

  const onPageHide = (apiRoot: string) => () => {
    beaconFlush(apiRoot);
  };

  let removeVisibilityListener: (() => void) | undefined;
  let removePageHideListener: (() => void) | undefined;

  const start = (params: { sessionId: string; apiRoot: string }): void => {
    if (isRecording.value) return;
    sessionId = params.sessionId;

    stopFn = record({
      emit: (event: eventWithTime) => {
        buffer.push(event);
        if (buffer.length >= 100) {
          void flush();
        }
      },
      // rrweb masks passwords by default; we leave other inputs visible
      // because the data is synthetic and the recordings are the whole
      // research point.
      recordCanvas: false,
    });

    intervalId = setInterval(() => {
      void flush();
    }, BATCH_SEND_INTERVAL_MS);

    const visibilityHandler = onVisibilityHidden(params.apiRoot);
    const pageHideHandler = onPageHide(params.apiRoot);
    document.addEventListener('visibilitychange', visibilityHandler);
    window.addEventListener('pagehide', pageHideHandler);
    removeVisibilityListener = () =>
      document.removeEventListener('visibilitychange', visibilityHandler);
    removePageHideListener = () => window.removeEventListener('pagehide', pageHideHandler);

    isRecording.value = true;
    try {
      // Scope the marker to THIS session id, not a bare `'true'`. The key is
      // plain localStorage (survives reloads and new sessions) and is not in
      // `clearChallengeStorage()`, so a bare boolean would leak across
      // sessions: a second challenge in the same browser would skip the
      // consent gate, never POST `/consent` for the new session, and every
      // replay batch would then 403 on the backend's consent guard.
      window.localStorage.setItem(CONSENT_ACCEPTED_KEY, params.sessionId);
    } catch {
      // localStorage can be unavailable in private windows; recording still works.
    }
  };

  const stop = (): void => {
    if (!isRecording.value) return;
    if (intervalId !== undefined) {
      clearInterval(intervalId);
      intervalId = undefined;
    }
    if (stopFn) {
      stopFn();
      stopFn = undefined;
    }
    if (removeVisibilityListener) {
      removeVisibilityListener();
      removeVisibilityListener = undefined;
    }
    if (removePageHideListener) {
      removePageHideListener();
      removePageHideListener = undefined;
    }
    isRecording.value = false;
    void flush();
  };

  onBeforeUnmount(() => {
    stop();
  });

  return {
    isRecording,
    start,
    stop,
    flush,
  };
}

export const challengeConsentAcceptedKey = CONSENT_ACCEPTED_KEY;
